/* Torabhaig age gate v2. No third-party libraries; no date of birth is stored or sent. */
(() => {
  const KEY = 'torabhaig-age-gate-v2';
  const GEO_KEY = 'torabhaig-age-gate-geo';
  const DAY = 86400000;
  const MAX_AGE_MS = 91 * DAY;

  function decision(year, month, day, minimum, now = new Date()) {
    if (!Number.isInteger(year) || year < 1900 || year > now.getFullYear()) return 'invalid';
    const difference = now.getFullYear() - year;
    if (difference > minimum) return 'pass';
    if (difference < minimum) return 'under';
    if (!month || !day) return 'details';
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return 'invalid';
    return month < now.getMonth() + 1 || (month === now.getMonth() + 1 && day <= now.getDate()) ? 'pass' : 'under';
  }

  function parseRules(text) {
    const rules = new Map();
    String(text || '').split(/\r?\n/).forEach(line => {
      if (!line.trim()) return;
      const [rawCode, name, rawAge] = line.split('|').map(s => (s || '').trim());
      const code = rawCode.toUpperCase();
      const age = Number(rawAge);
      if (/^[A-Z]{2}$/.test(code) && name && Number.isInteger(age) && age >= 18 && age <= 99) rules.set(code, { name, age });
    });
    return rules;
  }

  function readRecord(rules, now = Date.now()) {
    for (const store of ['sessionStorage', 'localStorage']) {
      try {
        const raw = window[store].getItem(KEY);
        if (!raw) continue;
        const r = JSON.parse(raw);
        if (r && r.version === 2 && typeof r.expires === 'number' && r.expires > now && r.expires <= now + MAX_AGE_MS && rules.get(r.country)?.age === r.age) return r;
        window[store].removeItem(KEY);
      } catch (_) { /* Storage is optional. */ }
    }
    return null;
  }

  function clearRecord() {
    for (const store of ['sessionStorage', 'localStorage']) {
      try { window[store].removeItem(KEY); } catch (_) { /* ignore */ }
    }
  }

  function fmt(text, vars) {
    return String(text || '').replace(/\[(age|days)\]/g, (_, k) => (vars[k] ?? ''));
  }

  // Detection is cached for the browser session so it runs once, not on every page.
  async function detectCountry(signal) {
    try {
      const cached = sessionStorage.getItem(GEO_KEY);
      if (cached !== null) return cached || null;
    } catch (_) { /* ignore */ }
    let code = null;
    try {
      const response = await fetch('/browsing_context_suggestions.json?country[enabled]=true', { signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (response.ok) {
        const data = await response.json();
        const handle = data?.detected_values?.country?.handle;
        if (typeof handle === 'string' && /^[A-Za-z]{2}$/.test(handle)) code = handle.toUpperCase();
      }
    } catch (_) {
      return null; // Network error or timeout: do not cache, try again on the next page.
    }
    try { sessionStorage.setItem(GEO_KEY, code || ''); } catch (_) { /* ignore */ }
    return code;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { decision, parseRules, readRecord, clearRecord, fmt, KEY };
    return;
  }
  if (customElements.get('torabhaig-age-gate')) return;

  customElements.define('torabhaig-age-gate', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.controller = new AbortController();
      const on = (node, event, fn) => node.addEventListener(event, fn, { signal: this.controller.signal });
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.t = this.config.text || {};
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.country = this.querySelector('[data-country]');
      this.year = this.querySelector('[data-year]');
      this.month = this.querySelector('[data-month]');
      this.day = this.querySelector('[data-day]');
      this.details = this.querySelector('[data-details]');
      this.error = this.querySelector('[data-error]');
      this.status = this.querySelector('[data-status]');
      this.threshold = this.querySelector('[data-threshold]');
      this.submit = this.querySelector('[data-submit]');
      this.rules = parseRules(this.config.rules);

      for (const [code, rule] of this.rules) this.country.add(new Option(rule.name, code));
      const currentYear = new Date().getFullYear();
      for (let y = currentYear; y >= 1900; y--) this.year.add(new Option(String(y), String(y)));
      const locale = document.documentElement.lang || 'en-GB';
      for (let m = 1; m <= 12; m++) {
        let label;
        try { label = new Date(2000, m - 1, 1).toLocaleString(locale, { month: 'long' }); } catch (_) { label = String(m); }
        this.month.add(new Option(label, String(m)));
      }
      for (let d = 1; d <= 31; d++) this.day.add(new Option(String(d), String(d)));

      on(this.dialog, 'cancel', event => event.preventDefault());
      on(this.country, 'change', () => {
        this.manual = true;
        this.submit.disabled = false;
        this.status.textContent = this.t.status_manual || '';
        this.update();
      });
      on(this.year, 'change', () => this.update());
      on(this.month, 'change', () => { this.limitDays(); this.error.textContent = ''; });
      on(this.day, 'change', () => { this.error.textContent = ''; });
      on(this.form, 'submit', event => { event.preventDefault(); this.check(); });

      this.dialog.removeAttribute('open');
      this.update();

      if (this.config.editor) {
        this.open();
        this.status.textContent = this.t.status_editor || '';
        this.submit.disabled = false;
        return;
      }

      const saved = readRecord(this.rules);
      if (saved) {
        // Returning visitor: no gate. Check country quietly in the background.
        document.documentElement.classList.add('tag-passed');
        this.recheck(saved);
        return;
      }
      document.documentElement.classList.remove('tag-passed');
      this.open();
      this.status.textContent = this.t.status_checking || '';
      this.detect();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.request?.abort();
      this.unlock();
      this.ready = false;
    }

    open() {
      if (!this.dialog.open) this.dialog.showModal();
      if (!this.locked) {
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.locked = true;
      }
    }

    unlock() {
      if (this.locked) document.documentElement.style.overflow = this.previousOverflow || '';
      this.locked = false;
    }

    get age() { return this.rules.get(this.country.value)?.age; }

    async lookup() {
      this.request = new AbortController();
      const timer = setTimeout(() => this.request.abort(), 3000);
      try { return await detectCountry(this.request.signal); } finally { clearTimeout(timer); }
    }

    async detect() {
      const detected = await this.lookup();
      if (!this.isConnected || this.manual || !this.dialog.open) return;
      this.apply(detected);
    }

    // Only re-prompt when detection positively reports a country that differs from
    // both the country chosen and the country detected when the visitor passed.
    async recheck(saved) {
      const detected = await this.lookup();
      if (!this.isConnected || !detected) return;
      if (detected === saved.country || detected === saved.detected) return;
      clearRecord();
      document.documentElement.classList.remove('tag-passed');
      this.open();
      this.apply(detected);
    }

    apply(detected) {
      this.detected = detected || null;
      if (detected && this.rules.has(detected)) {
        this.country.value = detected;
        this.status.textContent = this.t.status_detected || '';
      } else {
        this.country.value = '';
        this.status.textContent = (detected ? this.t.status_unlisted : this.t.status_unavailable) || '';
      }
      this.submit.disabled = false;
      this.update();
    }

    update() {
      this.error.textContent = '';
      this.threshold.textContent = this.age ? fmt(this.t.threshold, { age: this.age }) : (this.t.threshold_prompt || '');
      const required = !!this.age && Number(this.year.value) === new Date().getFullYear() - this.age;
      if (this.details.hidden === required) { this.month.value = ''; this.day.value = ''; }
      this.details.hidden = !required;
      this.month.required = required; this.day.required = required;
      this.month.disabled = !required; this.day.disabled = !required;
      this.limitDays();
    }

    limitDays() {
      const max = this.year.value && this.month.value ? new Date(Number(this.year.value), Number(this.month.value), 0).getDate() : 31;
      [...this.day.options].forEach(option => {
        if (!option.value) return;
        option.disabled = Number(option.value) > max;
        option.hidden = Number(option.value) > max;
      });
      if (Number(this.day.value) > max) this.day.value = '';
    }

    check() {
      if (!this.age) { this.error.textContent = this.t.error_country || ''; this.country.focus(); return; }
      const result = decision(Number(this.year.value), Number(this.month.value), Number(this.day.value), this.age);
      if (result !== 'pass') {
        const key = result === 'under' ? 'error_under' : result === 'details' ? 'error_details' : 'error_invalid';
        this.error.textContent = fmt(this.t[key], { age: this.age });
        return;
      }
      if (!this.config.editor) {
        const remember = this.querySelector('[data-remember]').checked;
        const days = Math.min(Math.max(Number(this.config.days) || 30, 1), 90);
        const record = { version: 2, country: this.country.value, age: this.age, detected: this.detected || null, expires: Date.now() + (remember ? days : 1) * DAY };
        clearRecord();
        try { window[remember ? 'localStorage' : 'sessionStorage'].setItem(KEY, JSON.stringify(record)); } catch (_) { /* Still allow entry. */ }
        document.documentElement.classList.add('tag-passed');
      }
      this.close();
    }

    close() {
      this.dialog.close();
      this.unlock();
      const main = document.getElementById('MainContent') || document.querySelector('main');
      if (main) {
        const previous = main.getAttribute('tabindex');
        main.setAttribute('tabindex', '-1');
        main.focus({ preventScroll: true });
        if (previous === null) main.removeAttribute('tabindex'); else main.setAttribute('tabindex', previous);
      }
    }
  });
})();
